import { Component, Input } from '@angular/core';
import { ButtonCellValue, EasTable, EasTableColumn, EasTableRow } from '@fry/lib/store';
import { EmptyProgress, ExportState, Progress, QueuedProgress, TableExportsService } from './table-exports.service';
import { BehaviorSubject, Subject, catchError, of, switchMap, takeUntil } from 'rxjs';
import { NotifyService } from '@fry/lib/utils';


export interface TableExportOptions {
  exportOnlySelected: boolean;
}

@Component({
    selector: 'eas-table-exports',
    templateUrl: './table-exports.component.html',
    styleUrls: ['./table-exports.component.css', '../table-controls.common.css'],
    standalone: false
})
export class TableExportsComponent {
  @Input() total: number = 0;
  @Input() itemLabels: Record<'single' | 'plural', string> = { single: 'item', plural: 'items' };

  destroyed = new Subject<void>;

  currentExportId$ = new BehaviorSubject<string | null>(null);
  downloadableExportId$ = new BehaviorSubject<string | null>(null);
  currentProgress$ = new BehaviorSubject<Progress>(EmptyProgress);

  // run a new export
  options: TableExportOptions = {exportOnlySelected: false};

  // previous exports
  showPreviousExports: boolean = false;
  tableRows: EasTableRow[] = [];
  tableAllAvailableColumns: EasTableColumn[] = [];
  tableSelectedColumns: string[] = [];

  constructor(
    private tableExportsService: TableExportsService,
    private notify: NotifyService
  ) {}

  ngOnInit() {
    // this.startProgressChecking();
    this.setupProgressChecking();
    this.tableExportsService.fetchPreviousTaskTable$().subscribe((easTable: EasTable) => {
      if (easTable.data.length > 0) {
        const firstRow = easTable.data[0];
        if (firstRow.row.state === 'progress') {
          this.currentExportId$.next(firstRow.id);
        }
        if (firstRow.row.state === 'success') {
          // this.currentExportId$.next(firstRow.id);
          this.downloadableExportId$.next(firstRow.id);
        }
      }
      this.tableRows = easTable.data;
    });
  }

  setupProgressChecking() {
    this.currentExportId$.pipe(
      takeUntil(this.destroyed),
      switchMap(taskId => {
        if (taskId === null) {
          return of(EmptyProgress);
        }

        return this.tableExportsService.getExportProgress(taskId);
      }),
      catchError(err => {
        console.log(err);
        return of(EmptyProgress);
      }),
      takeUntil(this.destroyed),
    ).subscribe({
      next: progress => {
        this.currentProgress$.next(progress);
        if (progress.state === ExportState.Completed) {
          this.downloadableExportId$.next(this.currentExportId$.value);
        }
      },
      complete: () => {
        this.tableExportsService.stopExportProgress();
      }
    });
  }

  // current export
  onExport() {
    if (![ExportState.Completed, ExportState.None, ExportState.Aborted].includes(this.currentProgress$.value.state)) {
      this.notify.error($localize `Export is already in progress`);
      return;
    }
    this.currentProgress$.next(QueuedProgress);
    this.tableExportsService.startExport$(this.options).subscribe(res => {
      this.currentExportId$.next(res.taskId);
      // this.startProgressChecking();
    });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  onDownloadLast(taskId: string) {
    return this.tableExportsService.download(taskId)
    .subscribe({
      error: err => {
        this.notify.error(err);
      }
    })
  }

  // previous exports
  toggleExportsView() {
    this.showPreviousExports = !this.showPreviousExports;
  }

  onSeePreviousExports() {
    this.tableExportsService.fetchPreviousTaskTable$().subscribe((easTable: EasTable) => {
      this.tableRows = easTable.data;
      this.tableAllAvailableColumns = easTable.columns;
      this.tableSelectedColumns = easTable.meta?.selectedColumns || easTable.columns.map(col => col.id);

      this.showPreviousExports = true;
    });
  }

  onSeeNewExport() {
    this.showPreviousExports = false;
  }

  onClick(cellValue: ButtonCellValue) {
    cellValue.onClick().subscribe()
  }
}
